import { z } from 'zod'
import type { SchoolPayload } from './api'
import type { SchoolSettings } from './types'

export const ACADEMIC_YEAR_PATTERNS = [
  { value: 'jan_dec', label: 'January – December' },
  { value: 'apr_mar', label: 'April – March' },
  { value: 'jul_jun', label: 'July – June' },
  { value: 'sep_aug', label: 'September – August' },
] as const

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters.`)
    .transform((v) => (v === '' ? null : v))

const time = /^([01]\d|2[0-3]):[0-5]\d$/

const openingHourSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6),
    isOpen: z.boolean(),
    // Disabled (closed-day) inputs may report undefined, so accept a missing value.
    openTime: z
      .string()
      .nullish()
      .transform((v) => v ?? ''),
    closeTime: z
      .string()
      .nullish()
      .transform((v) => v ?? ''),
  })
  .superRefine((v, ctx) => {
    if (!v.isOpen) return
    if (!time.test(v.openTime))
      ctx.addIssue({ code: 'custom', path: ['openTime'], message: 'Enter an opening time.' })
    if (!time.test(v.closeTime))
      ctx.addIssue({ code: 'custom', path: ['closeTime'], message: 'Enter a closing time.' })
    else if (time.test(v.openTime) && v.closeTime <= v.openTime)
      ctx.addIssue({ code: 'custom', path: ['closeTime'], message: 'Must be after opening time.' })
  })
  .transform((v) => ({
    dayOfWeek: v.dayOfWeek,
    isOpen: v.isOpen,
    openTime: v.isOpen ? v.openTime : null,
    closeTime: v.isOpen ? v.closeTime : null,
  }))

export const schoolSchema = z.object({
  logoFileId: z.number().nullable(),
  name: z.string().trim().min(1, 'Enter the school name.').max(150, 'Use at most 150 characters.'),
  shortName: optionalText(50),
  motto: optionalText(150),
  establishedYear: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (/^\d{4}$/.test(v) && Number(v) >= 1800 && Number(v) <= new Date().getFullYear()),
      'Enter a year between 1800 and this year.',
    )
    .transform((v) => (v === '' ? null : Number(v))),
  institutionCodeLabel: z
    .string()
    .trim()
    .min(1, 'Enter a label, e.g. “Registration No.”.')
    .max(50, 'Use at most 50 characters.'),
  institutionCode: optionalText(50),
  email: z
    .union([z.literal(''), z.email('Enter a valid email address.').max(180)])
    .transform((v) => (v === '' ? null : v)),
  phone: optionalText(30),
  website: z
    .union([
      z.literal(''),
      z.url({ message: 'Enter a full URL, e.g. https://school.example.', protocol: /^https?$/ }).max(255),
    ])
    .transform((v) => (v === '' ? null : v)),
  address: optionalText(500),
  countryCode: z
    .string()
    .trim()
    .refine((v) => v === '' || /^[A-Za-z]{2}$/.test(v), 'Use a 2-letter ISO code, e.g. US.')
    .transform((v) => (v === '' ? null : v.toUpperCase())),
  currency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, 'Use a 3-letter ISO code, e.g. USD.')
    .transform((v) => v.toUpperCase()),
  timezone: z.string().trim().min(1, 'Enter a time zone, e.g. Africa/Nairobi.').max(64),
  locale: z.string().trim().min(2, 'Enter a locale, e.g. en or fr-FR.').max(10),
  academicYearPattern: z.enum(['jan_dec', 'apr_mar', 'jul_jun', 'sep_aug'], {
    error: 'Choose when the academic year runs.',
  }),
  openingHours: z.array(openingHourSchema).length(7),
  guardiansEnabled: z.boolean(),
  type: z.enum(['primary', 'secondary', 'combined', 'higher', 'training']),
  vocabulary: z.object({ learner: z.string(), year: z.string(), class: z.string(), section: z.string() }),
})

export type SchoolFormInput = z.input<typeof schoolSchema>
export type SchoolFormOutput = z.output<typeof schoolSchema>

export function toSchoolFormValues(s: SchoolSettings): SchoolFormInput {
  const hours = WEEKDAYS.map((_, day) => {
    const h = s.openingHours.find((o) => o.dayOfWeek === day)
    return {
      dayOfWeek: day,
      isOpen: h?.isOpen ?? false,
      openTime: h?.openTime ?? '',
      closeTime: h?.closeTime ?? '',
    }
  })
  const overrides: Partial<Record<'learner' | 'year' | 'class' | 'section', string>> = Array.isArray(
    s.vocabularyOverrides,
  )
    ? {}
    : s.vocabularyOverrides
  const pattern = ACADEMIC_YEAR_PATTERNS.find((p) => p.value === s.academicYearPattern)?.value ?? 'jan_dec'
  return {
    logoFileId: null,
    name: s.name,
    shortName: s.shortName ?? '',
    motto: s.motto ?? '',
    establishedYear: s.establishedYear?.toString() ?? '',
    institutionCodeLabel: s.institutionCodeLabel,
    institutionCode: s.institutionCode ?? '',
    email: s.email ?? '',
    phone: s.phone ?? '',
    website: s.website ?? '',
    address: s.address ?? '',
    countryCode: s.countryCode ?? '',
    currency: s.currency,
    timezone: s.timezone,
    locale: s.locale,
    academicYearPattern: pattern,
    openingHours: hours,
    guardiansEnabled: s.guardiansEnabled,
    type: s.type,
    vocabulary: {
      learner: overrides.learner ?? '',
      year: overrides.year ?? '',
      class: overrides.class ?? '',
      section: overrides.section ?? '',
    },
  }
}

/** Compile-time check that the form output is exactly what PUT /school expects. */
export const toSchoolPayload = (v: SchoolFormOutput): SchoolPayload => v
